import type {
  billingClientOptionSchema,
  BillingLinkDto,
  BillingSettingsDto,
  BillingSettingsInput,
  CreateBillingClientInput,
  createBillingClientResultSchema,
  CursorPage,
  EntitlementsDto,
  ownerInvitationResultSchema,
  PlatformAuditEventDto,
  PlatformLicenceInvoiceDto,
  PlatformPlan,
  PlatformRoleKey,
  ProvisionWorkspaceInput,
  ProvisionWorkspaceResult,
  RaiseLicenceInvoiceInput,
  raiseLicenceInvoiceResultSchema,
  RenewLicenceInput,
  ReplaceOwnerInvitationInput,
  SetPlanInput,
  UpdateLicenceInput,
  WorkspaceRowDto,
} from "@l2a/shared";
import type { QueryClient } from "@tanstack/react-query";
import type { z } from "zod";
import { api } from "@/lib/api";

/**
 * THE PLATFORM CONSOLE — Lean2Automate's own layer above the tenants.
 *
 * These calls go to `/api/platform/v1`, the fourth route family, which is
 * authenticated by the same session cookie but gated on membership of
 * `platform_admins`. An ordinary customer's session gets 403 from every one of
 * them, which is why `platformApi.me()` is what decides whether any of this
 * appears in the navigation at all.
 *
 * Every shape comes from the shared contracts (`@l2a/shared` platform.ts), so
 * the console and the API cannot drift. Keys all start with "platform"; each
 * mutation invalidates exactly what it changed (see `refreshWorkspace`).
 */

export type PlatformRole = PlatformRoleKey;

export interface PlatformMe {
  userId: string;
  role: PlatformRole;
}

/** One workspace with its licence and seats. */
export type Workspace = WorkspaceRowDto;
export type Entitlements = EntitlementsDto;
export type Plan = PlatformPlan;
export type BillingSettings = BillingSettingsDto;
export type BillingLink = BillingLinkDto;
export type BillingClientOption = z.infer<typeof billingClientOptionSchema>;
export type CreateBillingClientResult = z.infer<typeof createBillingClientResultSchema>;
export type RaiseLicenceInvoiceResult = z.infer<typeof raiseLicenceInvoiceResultSchema>;
export type OwnerInvitationResult = z.infer<typeof ownerInvitationResultSchema>;
export type PlatformAuditEvent = PlatformAuditEventDto;
export type PlatformLicenceInvoice = PlatformLicenceInvoiceDto;

export interface PlatformAdmin {
  userId: string;
  email: string;
  name: string;
  role: PlatformRole;
}

export interface PlatformTicket {
  id: string;
  number: string;
  subject: string;
  body: string;
  kind: string;
  severity: string;
  status: string;
  context: string | null;
  raisedBy: string | null;
  createdAt: string;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  messageCount: number;
  orgId: string;
  orgName: string;
}

export interface PlatformMessage {
  id: string;
  body: string;
  fromPlatform: boolean;
  author: string | null;
  internal: boolean;
  createdAt: string;
}

const P = "/api/platform/v1";

export const platformKeys = {
  me: () => ["platform", "me"] as const,
  workspaces: () => ["platform", "workspaces"] as const,
  workspace: (orgId: string) => ["platform", "workspace", orgId] as const,
  entitlements: (orgId: string) => ["platform", "entitlements", orgId] as const,
  plans: () => ["platform", "plans"] as const,
  admins: () => ["platform", "admins"] as const,
  tickets: (filter: Record<string, string | undefined>) => ["platform", "tickets", filter] as const,
  ticket: (id: string) => ["platform", "ticket", id] as const,
  audit: (orgId?: string) => ["platform", "audit", orgId ?? "all"] as const,
  billing: () => ["platform", "billing"] as const,
  billingLinks: () => ["platform", "billing", "links"] as const,
  billingClients: () => ["platform", "billing", "clients"] as const,
  licenceInvoices: (orgId?: string) => ["platform", "licence-invoices", orgId ?? "all"] as const,
};

export const platformApi = {
  me: (signal?: AbortSignal) => api.get<PlatformMe>(`${P}/me`, { skipAuthRedirect: true, ...(signal ? { signal } : {}) }),
  workspaces: (signal?: AbortSignal) => api.get<{ items: Workspace[] }>(`${P}/workspaces`, signal ? { signal } : {}),
  workspace: (orgId: string, signal?: AbortSignal) => api.get<Workspace>(`${P}/workspaces/${orgId}`, signal ? { signal } : {}),
  entitlements: (orgId: string, signal?: AbortSignal) =>
    api.get<Entitlements>(`${P}/workspaces/${orgId}/entitlements`, signal ? { signal } : {}),
  setPlan: (orgId: string, body: SetPlanInput) => api.post<Entitlements>(`${P}/workspaces/${orgId}/plan`, body),
  updateLicence: (orgId: string, patch: UpdateLicenceInput) => api.patch<Workspace>(`${P}/workspaces/${orgId}/licence`, patch),
  renewLicence: (orgId: string, body: RenewLicenceInput) => api.post<Workspace>(`${P}/workspaces/${orgId}/licence/renew`, body),
  suspend: (orgId: string, reason: string) => api.post<Entitlements>(`${P}/workspaces/${orgId}/suspend`, { reason }),
  resume: (orgId: string) => api.post<Entitlements>(`${P}/workspaces/${orgId}/resume`),

  /** Lean2Automate creates a workspace and invites its first Admin as the Owner. */
  provision: (body: ProvisionWorkspaceInput) => api.post<ProvisionWorkspaceResult>(`${P}/workspaces`, body),
  /** No address resends to the current one; a new `email` replaces it (and kills the old link). */
  ownerInvitation: (orgId: string, body: ReplaceOwnerInvitationInput) =>
    // `{}` rather than no body: Fastify refuses a JSON request whose body is empty.
    api.post<OwnerInvitationResult>(`${P}/workspaces/${orgId}/owner-invitation`, body),

  audit: (filter: { orgId?: string; cursor?: string | null; limit?: number }, signal?: AbortSignal) =>
    api.get<CursorPage<PlatformAuditEvent>>(`${P}/audit`, {
      query: { orgId: filter.orgId, cursor: filter.cursor ?? undefined, limit: filter.limit },
      ...(signal ? { signal } : {}),
    }),

  billing: (signal?: AbortSignal) => api.get<BillingSettings>(`${P}/billing`, signal ? { signal } : {}),
  saveBilling: (body: BillingSettingsInput) => api.put<BillingSettings>(`${P}/billing`, body),
  billingClients: (q: string, signal?: AbortSignal) =>
    api.get<{ items: BillingClientOption[] }>(`${P}/billing/clients`, { query: { q }, ...(signal ? { signal } : {}) }),
  createBillingClient: (body: CreateBillingClientInput) => api.post<CreateBillingClientResult>(`${P}/billing/clients`, body),
  billingLinks: (signal?: AbortSignal) => api.get<{ items: BillingLink[] }>(`${P}/billing/links`, signal ? { signal } : {}),
  linkBillingClient: (orgId: string, clientId: string | null) =>
    api.put<BillingLink>(`${P}/workspaces/${orgId}/billing-client`, { clientId }),

  /**
   * Raises a licence invoice in the billing workspace. The key is created once
   * per opening of the dialog and reused for every retry, so a double click or
   * a dropped response replays the same invoice and number.
   */
  raiseInvoice: (orgId: string, body: RaiseLicenceInvoiceInput, idempotencyKey: string) =>
    api.post<RaiseLicenceInvoiceResult>(`${P}/licences/${orgId}/invoices`, body, { idempotencyKey }),
  licenceInvoices: (filter: { orgId?: string }, signal?: AbortSignal) =>
    api.get<{ items: PlatformLicenceInvoice[] }>(`${P}/licence-invoices`, {
      query: { orgId: filter.orgId },
      ...(signal ? { signal } : {}),
    }),

  plans: (signal?: AbortSignal) => api.get<{ items: Plan[] }>(`${P}/plans`, signal ? { signal } : {}),
  admins: (signal?: AbortSignal) => api.get<{ items: PlatformAdmin[] }>(`${P}/admins`, signal ? { signal } : {}),
  grantAdmin: (email: string, role: PlatformRole) => api.post<void>(`${P}/admins`, { email, role }),
  revokeAdmin: (userId: string) => api.delete(`${P}/admins/${userId}`),
  tickets: (filter: { status?: string; kind?: string }, signal?: AbortSignal) =>
    api.get<{ items: PlatformTicket[] }>(`${P}/tickets`, {
      query: { status: filter.status, kind: filter.kind },
      ...(signal ? { signal } : {}),
    }),
  ticket: (id: string, signal?: AbortSignal) =>
    api.get<{ ticket: PlatformTicket; messages: PlatformMessage[] }>(`${P}/tickets/${id}`, signal ? { signal } : {}),
  reply: (id: string, body: { body: string; internal?: boolean; status?: string }) =>
    api.post<PlatformMessage>(`${P}/tickets/${id}/messages`, body),
  triage: (id: string, patch: { status?: string; severity?: string; kind?: string; assignedToUserId?: string | null }) =>
    api.patch<void>(`${P}/tickets/${id}`, patch),
  upsertArticle: (article: {
    slug: string;
    title: string;
    category: string;
    summary: string | null;
    body: string;
    tags: string[];
    isPublished?: boolean;
    sortOrder?: number;
  }) => api.put<void>(`${P}/help-articles`, article),
};

/**
 * After anything that changes one workspace's licence, link or invoices: the
 * list, the row, its entitlements, its history and its invoices. Both trails
 * are written in the same transaction as the change, so they are worth
 * re-reading at once.
 */
export async function refreshWorkspace(queryClient: QueryClient, orgId: string): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: platformKeys.workspaces() }),
    queryClient.invalidateQueries({ queryKey: platformKeys.workspace(orgId) }),
    queryClient.invalidateQueries({ queryKey: platformKeys.entitlements(orgId) }),
    queryClient.invalidateQueries({ queryKey: ["platform", "audit"] }),
    queryClient.invalidateQueries({ queryKey: ["platform", "licence-invoices"] }),
    queryClient.invalidateQueries({ queryKey: platformKeys.billingLinks() }),
  ]);
}

/** Minor units → "₹4,999.00", or "Bespoke" when there is no list price. */
export function formatPrice(minor: number | null, currency: string): string {
  if (minor === null) return "Bespoke";
  if (minor === 0) return "Free";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 2 }).format(minor / 100);
}
